import * as recommendations from '@noths/polaris-client-recommendations';
import {
  addRUMTiming,
  getProductIdFromProductCode,
  handleFetchResponse,
} from '@noths/polaris-client-utils';
import { RecommendationsServiceProduct, RecommendedProduct } from '@noths/polaris-dev-ts-types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import fetch from 'isomorphic-fetch';
import getConfig from 'next/config';

import { Placement } from 'src/constants/recommendations';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { NextConfig, ProductPlacementRecord, ProductPlacementRecordError } from 'src/types';
import { logger } from 'src/utils';

const { endpoints } = (getConfig() as NextConfig).publicRuntimeConfig;

const transformToRecommendedProduct = (
  products: RecommendationsServiceProduct[],
  placementItems: recommendations.RichRelevancePlacementItem[],
): RecommendedProduct[] =>
  products.map((product) => {
    const placementItem = placementItems.find(
      ({ id }) => getProductIdFromProductCode(product.code) === Number(id),
    );

    return {
      ...product,
      linkURL: placementItem?.linkURL ?? '',
    };
  });

export const requestRecommendedProducts = createAsyncThunk<
  ProductPlacementRecord | null,
  recommendations.RichRelevancePlacement,
  {
    rejectValue: ProductPlacementRecordError;
  }
>(
  'richRelevance/requestRecommendedProducts',
  async ({ placement_name, items = [], message, strategy }, thunkApi) => {
    const ids = items.map((p) => p.id).join('&ids[]=');

    try {
      const apiResponse = await fetch(`${endpoints.recommendedProducts}?ids[]=${ids}`);

      const { products } = ((await handleFetchResponse(apiResponse)) || {}) as Record<
        'products',
        RecommendationsServiceProduct[]
      >;

      const recommendedProducts = transformToRecommendedProduct(products, items);

      return {
        products: recommendedProducts,
        placementType: placement_name as Placement,
        placementTitle: message,
        placementStrategy: strategy,
      };
    } catch (err) {
      const error = err as Error;

      logger.error({
        location: 'requestRecommendedProducts',
        extra: { description: `Exception thrown calling handleFetchResponse.`, error },
      });

      addRUMTiming('recommendationsError');

      return thunkApi.rejectWithValue({
        error: { message: error.message, stack: error.stack, name: error.name },
        placementType: placement_name,
      } as ProductPlacementRecordError);
    }
  },
);

export const requestCrossSellProducts = createAsyncThunk(
  'richRelevance/requestCrossSellProducts',
  async (_, { getState, dispatch }) => {
    const { product } = getState() as ReduxApplicationState;

    recommendations.setProductDetailPage(product.code, product.title);

    const placementData = await recommendations.getRecommendations([Placement.CrossSell]);
    const crossSellPlacement = placementData.find(
      (placement) => placement.placement_name === Placement.CrossSell,
    );

    if (!crossSellPlacement) {
      return null;
    }

    const placementRecord = await dispatch(requestRecommendedProducts(crossSellPlacement)).unwrap();

    return placementRecord;
  },
);
